<template>
  <div class="smartsheetform">
    <div class="items smartsheetform">
      <div class="logo-wrap">
        <img
          src="@/assets/images/resmed-logo-color.png"
          alt="Resmed product support"
          class="img-fluid mb-3"
        />
      </div>

      <div class="title mb-4">Astral-2026-FSN</div>

      <p class="text-center fw-bold subtitle">
        This form is to report devices that are no longer in use and do not
        require correction per Astral-2026-FSN
      </p>

      <!-- Privacy Notice -->
      <div class="privacy-notice">
        <strong>Privacy Notice: </strong>
        All information you provide in this form will only be used to identify
        and process devices that are no longer in use, so they can be removed
        from the active fleet and appropriately recorded and managed. All
        information provided will be processed in accordance with the Resmed
        <a
          href="https://www.resmed.co.uk/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          >Privacy Policy</a
        >.
      </div>

      <div class="item card-white">
        <!-- BULK UPLOAD SECTION -->
        <div class="section-card">
          <div class="section-header" @click="bulkOpen = !bulkOpen">
            <span>Report Device Status of Multiple Serial Numbers</span>
            <span class="chevron">
              <img
                v-if="bulkOpen"
                src="./assets/images/icon-down-chevron-red.svg"
                alt="Expand"
              />
              <span v-else
                ><img
                  src="./assets/images/icon-up-chevron-red.svg"
                  alt="Collapse"
              /></span>
            </span>
          </div>

          <div v-if="bulkOpen" class="section-body">
            <!-- Link to download -->
            <span class="mb-2 text-md"
              >To bulk report the status of <strong>multiple devices</strong>,
              either</span
            >
            <ol class="ml-4 mb-2 text-md" style="line-height: 1.9;">
              <li>
                <a
                  href="https://document.resmed.com/documents/astralfsn/Astral-2026-FSN_Affected_Device_Status_Return_Template.xlsx"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="brand-link"
                  >Download the template</a
                >, fill in the details and upload the file OR
              </li>
              <li>
                <a href="#" class="brand-link" @click.prevent="openManualTable">
                  Click here
                </a>
                to enter the information directly.
              </li>
            </ol>

            <!-- FILE UPLOAD mode -->
            <div v-if="!manualTableOpen">
              <!-- Drop zone -->
              <div
                v-if="bulkRows.length === 0"
                class="drop-zone"
                :class="{ 'drop-zone--active': dragOver }"
                @dragover.prevent="dragOver = true"
                @dragleave="dragOver = false"
                @drop.prevent="onDrop"
                @click="$refs.fileInput.click()"
              >
                <div class="drop-icon">&#9729;</div>
                <p>Choose a file or Drag and drop file here&hellip;</p>
                <p class="drop-sub">Supports .xlsx, .xls, .csv</p>
                <input
                  ref="fileInput"
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  style="display: none"
                  @change="onFileChange"
                />
              </div>

              <!-- Error / warning -->
              <div v-if="bulkError" class="bulk-error">
                <strong
                  >&#9888;
                  {{
                    bulkError.startsWith("&#9888;") ? "" : "File Error"
                  }}</strong
                >
                <pre>{{ bulkError }}</pre>
              </div>

              <!-- Confirmation table after file parsed -->
              <div v-if="bulkRows.length > 0">
                <div v-if="parsedSheets.length" class="sheet-pills">
                  <span class="sheet-pills__label">Sheets parsed:</span>
                  <span v-for="s in parsedSheets" :key="s" class="sheet-pill">{{
                    s
                  }}</span>
                </div>

                <p class="confirm-title">
                  Please confirm the data before submitting &hellip;
                </p>

                <div class="table-wrap">
                  <table class="bulk-table">
                    <thead>
                      <tr>
                        <th>Source Sheet</th>
                        <th v-for="col in bulkColumns" :key="col">{{ col }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, i) in bulkRows"
                        :key="i"
                        :class="i % 2 === 0 ? 'row-even' : 'row-odd'"
                      >
                        <td>
                          <span class="sheet-badge">{{
                            row._sheet || "&mdash;"
                          }}</span>
                        </td>
                        <td v-for="col in bulkColumns" :key="col">
                          {{
                            row[col] !== null && row[col] !== undefined
                              ? row[col]
                              : "&mdash;"
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p class="record-count">
                  {{ bulkRows.length }} record(s) across
                  {{ parsedSheets.length }} sheet(s) ready to submit.
                </p>

                <div class="bulk-actions">
                  <button
                    class="submit-btn"
                    :disabled="bulkSubmitting"
                    @click="submitBulk"
                  >
                    {{ bulkSubmitting ? "Submitting&hellip;" : "Submit" }}
                  </button>
                  <button class="reset-btn" @click="resetBulk">Reset</button>
                </div>
              </div>

              <!-- OR + manual link -->
              <div v-if="bulkRows.length === 0" class="file-or-manual">
                <button
                  class="submit-btn"
                  style="margin-top: 8px;"
                  @click="$refs.fileInput.click()"
                >
                  Submit
                </button>
              </div>
            </div>

            <!-- MANUAL ENTRY TABLE mode -->
            <div v-if="manualTableOpen" class="manual-table-wrap">
              <div class="table-wrap">
                <table class="bulk-table manual-table">
                  <thead>
                    <tr>
                      <th>Device Serial Number</th>
                      <th>Device Status</th>
                      <th>Company Name</th>
                      <th>Contact Email</th>
                      <th>Comments</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in manualRows" :key="i">
                      <td>
                        <input
                          v-model="row['Device Serial Number']"
                          class="cell-input"
                          :class="{ 'cell-input--error': duplicateSerialIndices.has(i) }"
                          placeholder="Serial number"
                        />
                      </td>
                      <td>
                        <select
                          v-model="row['Device Status']"
                          class="cell-select"
                        >
                          <option value="" disabled>Select status</option>
                          <option
                            v-for="opt in manualStatusOptions"
                            :key="opt"
                            :value="opt"
                            >{{ opt }}</option
                          >
                        </select>
                      </td>
                      <td>
                        <input
                          v-model="row['Company Name']"
                          class="cell-input"
                          placeholder="Customer"
                        />
                      </td>
                      <td>
                        <input
                          v-model="row['Contact Email']"
                          class="cell-input"
                          placeholder="Email"
                          type="email"
                        />
                      </td>
                      <td>
                        <input
                          v-model="row['Comments']"
                          class="cell-input"
                          placeholder="Comments"
                        />
                      </td>
                      <td class="remove-cell">
                        <button
                          v-if="manualRows.length > 1"
                          class="remove-row-btn"
                          @click="removeManualRow(i)"
                          title="Remove row"
                        >
                          &#10005;
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                v-if="manualError"
                class="bulk-error"
                style="margin-top: 8px;"
              >
                <strong>&#9888; Validation Error</strong>
                <pre>{{ manualError }}</pre>
              </div>

              <div class="manual-table-footer">
                <button
                  class="add-row-btn"
                  @click="addManualRow"
                  title="Add row"
                >
                  +
                </button>
                <div class="bulk-actions">
                  <button
                    class="submit-btn"
                    :disabled="manualSubmitting"
                    @click="submitManualTable"
                  >
                    {{ manualSubmitting ? "Submitting&hellip;" : "Submit" }}
                  </button>
                  <button class="reset-btn" @click="closeManualTable">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- OR divider -->
        <div class="or-divider">
          <div class="or-line"></div>
          <span class="or-text">OR</span>
          <div class="or-line"></div>
        </div>

        <!-- SINGLE ENTRY SECTION -->
        <div class="section-card">
          <div class="section-header" @click="singleOpen = !singleOpen">
            <span>Report Device Status of One Serial Number</span>
            <span class="chevron">
              <img
                v-if="singleOpen"
                src="./assets/images/icon-down-chevron-red.svg"
                alt="Expand"
              />
              <span v-else
                ><img
                  src="./assets/images/icon-up-chevron-red.svg"
                  alt="Collapse"
              /></span>
            </span>
          </div>

          <div v-if="singleOpen" class="section-body">
            <form @submit.prevent="submit">
              <div class="form-container">
                <div class="input-div">
                  <label for="contact_email" class="mandatory"
                    >Contact email</label
                  >
                  <input
                    id="contact_email"
                    v-model="contact_email"
                    type="text"
                    name="contact_email"
                    class="input-full"
                    required
                  />
                  <span v-if="validation.contact_email" class="error">{{
                    validation.contact_email
                  }}</span>
                </div>

                <div class="input-div">
                  <label for="companyName" class="mandatory"
                    >Your Company Name</label
                  >
                  <input
                    id="company_name"
                    v-model="companyName"
                    type="text"
                    name="companyName"
                    class="input-full"
                  />
                  <span v-if="validation.companyName" class="error">{{
                    validation.companyName
                  }}</span>
                </div>

                <div class="input-div">
                  <label for="device_serial_number" class="mandatory"
                    >Device Serial Number</label
                  >
                  <input
                    id="device_number"
                    v-model="device_serial_number"
                    type="text"
                    name="device_serial_number"
                    class="input-half"
                  />
                  <span v-if="validation.device_serial_number" class="error">{{
                    validation.device_serial_number
                  }}</span>
                </div>

                <!-- Device Status -->
                <div class="device-status-container">
                  <label for="deviceStatus" class="mandatory"
                    >Device Status</label
                  >
                  <div class="radio-row">
                    <input
                      type="radio"
                      id="ds2"
                      value="Device scrapped / decommissioned"
                      v-model="deviceStatus"
                    />
                    <label for="ds2">Device scrapped / decommissioned</label>
                  </div>
                  <div class="radio-row">
                    <input
                      type="radio"
                      id="ds3"
                      value="Device unable to be found"
                      v-model="deviceStatus"
                    />
                    <label for="ds3">Device unable to be found</label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="ds4"
                      value="No longer in managed fleet"
                      v-model="deviceStatus"
                    />
                    <label for="ds4">No longer in managed fleet</label>
                  </div>
                  <span v-if="validation.deviceStatus" class="error">{{
                    validation.deviceStatus
                  }}</span>
                </div>

                <div class="border-div"></div>

                <div class="radio-row">
                  <input
                    type="checkbox"
                    id="emailVerification"
                    v-model="emailVerification"
                  />
                  <label for="emailVerification"
                    >Send me a copy of my responses</label
                  >
                </div>

                <div
                  v-if="emailVerification"
                  class="device-Comments-container mt-3"
                >
                  <label for="emailComment" class="mandatory"
                    >Email address</label
                  >
                  <input
                    id="emailComment"
                    v-model="emailComment"
                    type="text"
                    name="emailComment"
                    class="input-full"
                  />
                  <span v-if="validation.emailComment" class="error">{{
                    validation.emailComment
                  }}</span>
                </div>

                <div class="submit-btn-wrap">
                  <button
                    type="submit"
                    class="submit-btn"
                    :disabled="status.submitting"
                  >
                    {{ status.submitting ? "Submitting…" : "Submit" }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const CANONICAL_COLS = {
  SERIAL: "Device Serial Number",
  STATUS: "Device Status",
  CUSTOMER: "Company Name",
  EMAIL: "Contact Email",
  COMMENTS: "Comments",
};

const SHEET_CONFIG = {
  "Astral Status Return (English)": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "en",
    colMap: {
      "Serial Number of Astral Ventilator": CANONICAL_COLS.SERIAL,
      "Status (select status of serial number from drop down) ": CANONICAL_COLS.STATUS,
      "Status (select status of serial number from drop down)": CANONICAL_COLS.STATUS,
      Customer: CANONICAL_COLS.CUSTOMER,
      Email: CANONICAL_COLS.EMAIL,
      Comments: CANONICAL_COLS.COMMENTS,
    },
  },

  "Astral Status Return (French)": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "fr",
    colMap: {
      "Numéro de série du ventilateur astral": CANONICAL_COLS.SERIAL,
      "Statut (sélectionnez le statut du numéro de série dans la liste déroulante)": CANONICAL_COLS.STATUS,
      Cliente: CANONICAL_COLS.CUSTOMER,
      Email: CANONICAL_COLS.EMAIL,
      Commentaires: CANONICAL_COLS.COMMENTS,
    },
  },

  "Astral Status Return (German)": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "de",
    colMap: {
      "Seriennummer des Astral Beatmungsgeräts": CANONICAL_COLS.SERIAL,
      "Status (wählen Sie den Status der Seriennummer aus der Dropdown-Liste)": CANONICAL_COLS.STATUS,
      Kunde: CANONICAL_COLS.CUSTOMER,
      Email: CANONICAL_COLS.EMAIL,
      Kommentar: CANONICAL_COLS.COMMENTS,
    },
  },

  "Astral Status Return (Polish)": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "pl",
    colMap: {
      "Numer seryjny respiratora Astral": CANONICAL_COLS.SERIAL,
      "Status (wybierz status numeru seryjnego z listy rozwijanej)": CANONICAL_COLS.STATUS,
      Klient: CANONICAL_COLS.CUSTOMER,
      "Adres Email": CANONICAL_COLS.EMAIL,
      Uwagi: CANONICAL_COLS.COMMENTS,
    },
  },

  "Devolução do estado do Astral ": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "pt",
    colMap: {
      "Número de série do ventilador Astral": CANONICAL_COLS.SERIAL,
      "Estado (selecionar o estado do número de série no menu pendente)": CANONICAL_COLS.STATUS,
      Cliente: CANONICAL_COLS.CUSTOMER,
      Email: CANONICAL_COLS.EMAIL,
      Observações: CANONICAL_COLS.COMMENTS,
    },
  },

  "Devolución del estado de Astral": {
    headerRow: 0,
    dataStartRow: 1,
    lang: "sp",
    colMap: {
      "Número de serie del ventilador Astral": CANONICAL_COLS.SERIAL,
      "Estado (seleccione el estado del número de serie en el menú desplegable)": CANONICAL_COLS.STATUS,
      Cliente: CANONICAL_COLS.CUSTOMER,
      Email: CANONICAL_COLS.EMAIL,
      Comentarios: CANONICAL_COLS.COMMENTS,
    },
  },
};

const STATUS_VALUE_MAP = {
  "decomissioned/scrapped": "Device scrapped / decommissioned",
  "decommissioned/scrapped": "Device scrapped / decommissioned",
  "cannot be found": "Device unable to be found",
  "no longer in managed fleet": "No longer in managed fleet",
  "mis hors service/mis au rebut": "Device scrapped / decommissioned",
  "n'est plus dans le parc actif": "No longer in managed fleet",
  "ne peut être trouvé": "Device unable to be found",
  verschrottet: "Device scrapped / decommissioned",
  "nicht auffindbar": "Device unable to be found",
  "nicht mehr in der verwaltung": "No longer in managed fleet",
  "wycofany/złomowany": "Device scrapped / decommissioned",
  "nie można znaleźć": "Device unable to be found",
  "nie znajduje się już w zarządzanej flocie": "No longer in managed fleet",
  "desmantelado/desguazado": "Device scrapped / decommissioned",
  "no se localiza": "Device unable to be found",
  "ya no forma parte de la flota gestionada": "No longer in managed fleet",
  "desativado/descarregado": "Device scrapped / decommissioned",
  "não pode ser encontrado": "Device unable to be found",
  "já não faz parte da frota gerida": "No longer in managed fleet",
};

const DEVICE_STATUS_OPTIONS = [
  "Device scrapped / decommissioned",
  "Device unable to be found",
  "No longer in managed fleet",
];

const KNOWN_SHEET_NAMES = Object.keys(SHEET_CONFIG);

const DISPLAY_COLS = [
  CANONICAL_COLS.SERIAL,
  CANONICAL_COLS.STATUS,
  CANONICAL_COLS.CUSTOMER,
  CANONICAL_COLS.EMAIL,
  CANONICAL_COLS.COMMENTS,
];

const emptyManualRow = () => ({
  "Device Serial Number": "",
  "Device Status": "",
  "Company Name": "",
  "Contact Email": "",
  Comments: "",
});

export default {
  name: "SmartSheetFormFSN",

  data() {
    return {
      // Single-entry fields
      contact_email: null,
      companyName: null,
      device_serial_number: null,
      deviceStatus: null,
      emailVerification: false,
      emailComment: null,
      validation: {},
      status: {
        submitting: false,
        success: false,
        errorMessage: null,
      },

      // Bulk upload
      bulkRows: [],
      bulkColumns: DISPLAY_COLS,
      parsedSheets: [],
      bulkError: null,
      bulkSubmitting: false,
      dragOver: false,

      // UI accordion
      bulkOpen: true,
      singleOpen: true,

      // Manual entry table
      manualTableOpen: false,
      manualSubmitting: false,
      manualError: null,
      manualStatusOptions: [
        "Device scrapped / decommissioned",
        "Device unable to be found",
         "No longer in managed fleet"
      ],
      manualRows: [emptyManualRow()],
    };
  },

  computed: {
    // Returns a Set of row indices that contain a duplicate serial number.
    // Used to highlight the offending cells in the manual table in real time.
    duplicateSerialIndices() {
      const seen = new Map(); // serial → first row index
      const dupes = new Set();
      this.manualRows.forEach((row, i) => {
        const serial = String(row["Device Serial Number"] || "").trim();
        if (!serial) return;
        if (seen.has(serial)) {
          dupes.add(i);
          dupes.add(seen.get(serial)); // also mark the original row
        } else {
          seen.set(serial, i);
        }
      });
      return dupes;
    },
  },

  methods: {
    mapRowToPayload(row) {
      return {
        contact_email: row["Contact Email"] || "",
        company_name: row["Company Name"] || "",
        device_serial_number: row["Device Serial Number"] || "",
        device_status: row["Device Status"] || "",
        lang: row["lang"] || "",
        send_email: false,
        email_address: "",
        comments: row["Comments"] || "",
      };
    },

    validateField(fieldName) {
      const value = this[fieldName];
      let error = null;
      switch (fieldName) {
        case "contact_email":
          if (!value) error = "Email is required";
          else if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).toLowerCase())
          )
            error = "Invalid email format";
          break;
        case "companyName":
          if (!value) error = "Company name is required";
          break;
        case "device_serial_number":
          if (!value) error = "Device serial number is required";
          else if (typeof value === "string" && !/^[0-9eE]*$/.test(value))
            error = "Device serial number should contain only digits or 'e'";
          break;
        case "deviceStatus":
          if (!value) error = "Device status is required";
          break;
        case "emailComment":
          if (this.emailVerification && !value)
            error = "Email address is required for email verification";
          break;
        default:
          break;
      }
      this.$set(this.validation, fieldName, error);
      return !error;
    },

    validateForm() {
      const fields = [
        "contact_email",
        "companyName",
        "device_serial_number",
        "deviceStatus",
        "emailComment",
      ];
      return fields.every((f) => this.validateField(f));
    },

    // ─── Single-entry submit ─────────────────────────────────────────────────
    async submit() {
      if (!this.validateForm()) return;
      this.status.submitting = true;
      this.status.success = false;
      this.status.errorMessage = null;
      const payload = {
        data: {
          contact_email: this.contact_email,
          company_name: this.companyName,
          device_serial_number: this.device_serial_number,
          device_status: this.deviceStatus,
          send_email: this.emailVerification,
          email_address: this.emailComment,
        },
      };
      for (const key in payload.data) {
        if (payload.data[key] === null) payload.data[key] = "";
      }
      try {
        const response = await fetch(
          `${
            process.env.VUE_APP_GPSPOD_ENDPOINT
          }/gps/home/uploadForm?astral=true`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          },
        );
        await response.json();
        this.status.success = true;
        Swal.fire({
          title: "Success!",
          text: "Form submitted successfully.",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch {
        this.status.errorMessage = "Failed to submit the form.";
        Swal.fire({
          title: "Error!",
          text: "Failed to submit the form.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.status.submitting = false;
      }
    },

    // ─── File handling ───────────────────────────────────────────────────────
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.parseExcelFile(file);
    },

    onDrop(e) {
      this.dragOver = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.parseExcelFile(file);
    },

    parseExcelFile(file) {
      this.bulkError = null;
      this.bulkRows = [];
      this.parsedSheets = [];
      const validExts = [".xlsx", ".xls", ".csv"];
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!validExts.includes(ext)) {
        this.bulkError =
          "Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.";
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const matchedSheets = [];
          for (const name of workbook.SheetNames) {
            const trimmedName = name.trim();
            const configKey = KNOWN_SHEET_NAMES.find(
              (k) => k.trim().toLowerCase() === trimmedName.toLowerCase()
            );
            if (configKey) matchedSheets.push({ sheetName: name, configKey });
          }

          const usedFallback = matchedSheets.length === 0;
          const sheetsToProcess = usedFallback
            ? [{ sheetName: workbook.SheetNames[0], configKey: null }]
            : matchedSheets;

          const allRows = [];

          for (const { sheetName, configKey } of sheetsToProcess) {
            const sheet = workbook.Sheets[sheetName];
            const raw = XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              defval: null,
            });

            const config = configKey ? SHEET_CONFIG[configKey] : null;

            let headerRowIndex;
            let dataStartIndex;

            if (config) {
              headerRowIndex = config.headerRow;
              dataStartIndex = config.dataStartRow;
            } else {
              headerRowIndex = raw.findIndex(
                (r) => r && r[0] !== null && r[0] !== undefined && r[0] !== ""
              );
              if (headerRowIndex === -1) headerRowIndex = 0;
              dataStartIndex = headerRowIndex + 1;
            }

            if (raw.length <= headerRowIndex) continue;

            const headerRow = raw[headerRowIndex];
            const colMap = config ? config.colMap : {};

            const colIndex = {};
            headerRow.forEach((cell, idx) => {
              if (cell === null || cell === undefined) return;
              const canonical = colMap[String(cell).trim()];
              if (canonical) colIndex[idx] = canonical;
            });

            for (let r = dataStartIndex; r < raw.length; r++) {
              const rawRow = raw[r];
              if (!rawRow || rawRow.every((c) => c === null || c === ""))
                continue;

              const mapped = {};
              Object.entries(colIndex).forEach(([idxStr, canonical]) => {
                const val = rawRow[Number(idxStr)];
                mapped[canonical] = val !== undefined ? val : null;
              });

              if (
                mapped["Device Status"] !== null &&
                mapped["Device Status"] !== undefined
              ) {
                const rawStatus = String(mapped["Device Status"])
                  .trim()
                  .toLowerCase();
                const canonical = STATUS_VALUE_MAP[rawStatus];
                if (canonical) mapped["Device Status"] = canonical;
              }

              mapped["_sheet"] = sheetName;
              mapped["lang"] = config && config.lang ? config.lang : null;
              allRows.push(mapped);
            }
          }

          if (allRows.length === 0) {
            this.bulkError = usedFallback
              ? "No recognised sheet names found and the first sheet contains no data rows."
              : `The recognised sheet(s) were found but contained no data rows:\n${sheetsToProcess
                  .map((s) => s.sheetName)
                  .join(", ")}`;
            return;
          }

          // ── Row-level validation (includes duplicate serial detection) ────
          const rowErrors = [];
          const seenSerials = new Map(); // serial → "Record N [SheetName]"

          allRows.forEach((row, i) => {
            const label = ` [${row._sheet || "?"}]`;

            if (!row["Contact Email"])
              rowErrors.push(`Record ${i + 1}${label}: Email is required`);
            if (!row["Company Name"])
              rowErrors.push(
                `Record ${i + 1}${label}: Company Name is required`
              );

            if (!row["Device Serial Number"]) {
              rowErrors.push(
                `Record ${i + 1}${label}: Device Serial Number is required`
              );
            } else {
              const serial = String(row["Device Serial Number"]).trim();
              if (seenSerials.has(serial)) {
                rowErrors.push(
                  `Record ${i + 1}${label}: Duplicate serial number "${serial}" (first seen at ${seenSerials.get(serial)})`
                );
              } else {
                seenSerials.set(serial, `Record ${i + 1}${label}`);
              }
            }

            if (!row["Device Status"])
              rowErrors.push(
                `Record ${i + 1}${label}: Device Status is required`
              );
            else if (
              !DEVICE_STATUS_OPTIONS.includes(String(row["Device Status"]))
            )
              rowErrors.push(
                `Record ${i + 1}${label}: Unrecognised Device Status "${
                  row["Device Status"]
                }"`
              );
          });

          if (rowErrors.length > 0) {
            this.bulkError =
              rowErrors.slice(0, 5).join("\n") +
              (rowErrors.length > 5
                ? `\n…and ${rowErrors.length - 5} more issue(s).`
                : "");
            return;
          }

          if (usedFallback) {
            this.bulkError = `⚠ Warning: No recognised sheet names found. Using first sheet "${
              sheetsToProcess[0].sheetName
            }" as fallback.\nExpected one of:\n${KNOWN_SHEET_NAMES.join("\n")}`;
          }

          this.bulkRows = allRows;
          this.parsedSheets = sheetsToProcess.map((s) => s.sheetName);
        } catch (err) {
          console.error(err);
          this.bulkError =
            "Could not parse the file. Please ensure it is a valid Excel or CSV file.";
        }
      };
      reader.readAsBinaryString(file);
    },

    resetBulk() {
      this.bulkRows = [];
      this.parsedSheets = [];
      this.bulkError = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },

    // ─── Manual table ────────────────────────────────────────────────────────
    openManualTable() {
      this.manualTableOpen = true;
      this.manualError = null;
      this.manualRows = [emptyManualRow()];
    },

    closeManualTable() {
      this.manualTableOpen = false;
      this.manualError = null;
    },

    addManualRow() {
      this.manualRows.push(emptyManualRow());
    },

    removeManualRow(index) {
      this.manualRows.splice(index, 1);
    },

    // ── Manual table validation (includes duplicate serial detection) ────────
    validateManualRows() {
      const errors = [];
      const seen = new Map(); // serial → first row number (1-based)

      this.manualRows.forEach((row, i) => {
        if (!row["Device Serial Number"]) {
          errors.push(`Row ${i + 1}: Device Serial Number is required`);
        } else {
          const serial = String(row["Device Serial Number"]).trim();
          if (seen.has(serial)) {
            errors.push(
              `Row ${i + 1}: Duplicate serial number "${serial}" (already entered in Row ${seen.get(serial)})`
            );
          } else {
            seen.set(serial, i + 1);
          }
        }
        if (!row["Device Status"])
          errors.push(`Row ${i + 1}: Device Status is required`);
        if (!row["Contact Email"])
          errors.push(`Row ${i + 1}: Contact Email is required`);
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row["Contact Email"]))
          errors.push(`Row ${i + 1}: Contact Email is invalid`);
        if (!row["Company Name"])
          errors.push(`Row ${i + 1}: Company Name is required`);
      });
      return errors;
    },

    // ─── Manual table submit (uses mapRowToPayload) ──────────────────────────
    async submitManualTable() {
      this.manualError = null;
      const errors = this.validateManualRows();
      if (errors.length) {
        this.manualError =
          errors.slice(0, 5).join("\n") +
          (errors.length > 5
            ? `\n…and ${errors.length - 5} more issue(s).`
            : "");
        return;
      }
      this.manualSubmitting = true;
      try {
        const response = await fetch(
          `${
            process.env.VUE_APP_GPSPOD_ENDPOINT
          }/gps/home/uploadForm?astral=true&bulk=true`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              data: this.manualRows.map(this.mapRowToPayload),
            }),
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await response.json();
        Swal.fire({
          title: "Success!",
          text: `${this.manualRows.length} record(s) submitted successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        this.closeManualTable();
      } catch {
        Swal.fire({
          title: "Error!",
          text: "Failed to submit. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.manualSubmitting = false;
      }
    },

    // ─── File bulk submit (uses mapRowToPayload) ─────────────────────────────
    async submitBulk() {
      if (!this.bulkRows.length) return;
      this.bulkSubmitting = true;
      try {
        const response = await fetch(
          `${
            process.env.VUE_APP_GPSPOD_ENDPOINT
          }/gps/home/uploadForm?astral=true&bulk=true`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              data: this.bulkRows.map(this.mapRowToPayload),
            }),
          },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await response.json();
        Swal.fire({
          title: "Success!",
          text: `${this.bulkRows.length} record(s) submitted successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        });
        this.resetBulk();
      } catch {
        Swal.fire({
          title: "Error!",
          text: "Failed to submit bulk data. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
        });
      } finally {
        this.bulkSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ─── Base / reset ───────────────────────────────────────────────────────────*/
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* ─── Layout ─────────────────────────────────────────────────────────────────*/
.smartsheetform {
  padding: 16px;
}

.items.smartsheetform {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.logo-wrap {
  width: 160px;
  max-width: 100%;
  margin-bottom: 8px;
}
.logo-wrap img {
  width: 100%;
  height: auto;
}

/* card wrapper — fluid with a max-width */
.card-white {
  width: 100%;
  max-width: 700px;
}

/* ─── Typography ─────────────────────────────────────────────────────────────*/
.title {
  font-size: clamp(18px, 4vw, 28px);
  font-weight: bold;
  text-align: center;
  line-height: 1.3;
}
.subtitle {
  font-size: clamp(12px, 2.5vw, 14px);
  text-align: center;
  padding: 0 8px;
}

/* ─── Privacy notice ─────────────────────────────────────────────────────────*/
.privacy-notice {
  background: #f3e6ef;
  border: 1px solid #e0d0da;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #4a1a38;
  line-height: 1.6;
  width: 100%;
  max-width: 700px;
  text-align: left;
  word-break: break-word;
}
.privacy-notice a {
  color: #8b0057;
  word-break: break-all;
}

/* ─── Accordion ──────────────────────────────────────────────────────────────*/
.section-card {
  border: 1px solid #e0d0da;
  border-radius: 8px;
  margin: 1px;
  overflow: hidden;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #faf6f9;
  cursor: pointer;
  font-weight: 600;
  font-size: clamp(13px, 2.5vw, 15px);
  color: #333;
  border-bottom: 1px solid #e0d0da;
}
.chevron {
  font-size: 16px;
  color: #8b0057;
  flex-shrink: 0;
  margin-left: 8px;
}
.section-body {
  padding: 16px;
}

/* ─── OR dividers ────────────────────────────────────────────────────────────*/
.or-divider {
  display: flex;
  align-items: center;
  margin: 8px 0 16px;
}
.or-line {
  flex: 1;
  height: 1px;
  background: #e0d0da;
}
.or-text {
  padding: 0 12px;
  color: #aaa;
  font-size: 13px;
  font-weight: 600;
}

.file-or-manual {
  margin-top: 14px;
}
.brand-link {
  color: #8b0057;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

/* ─── Drop zone ──────────────────────────────────────────────────────────────*/
.drop-zone {
  border: 2px dashed #bbb;
  border-radius: 8px;
  background: #f9f9fb;
  padding: 28px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 12px;
  color: #666;
  font-size: 14px;
}
.drop-zone--active {
  border-color: #8b0057;
  background: #f3e6ef;
}
.drop-icon {
  font-size: 32px;
  margin-bottom: 6px;
}
.drop-sub {
  color: #999;
  font-size: 12px;
}

/* ─── Bulk error ─────────────────────────────────────────────────────────────*/
.bulk-error {
  background: #fdecea;
  border: 1px solid #f5c6c2;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
  color: #7b2020;
  font-size: 13px;
}
.bulk-error pre {
  margin: 4px 0 0;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* ─── Sheet pills ────────────────────────────────────────────────────────────*/
.sheet-pills {
  margin-bottom: 10px;
}
.sheet-pills__label {
  font-size: 12px;
  color: #666;
  margin-right: 6px;
}
.sheet-pill {
  display: inline-block;
  background: #f3e6ef;
  color: #8b0057;
  border: 1px solid #e0d0da;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 6px;
  margin-bottom: 4px;
}

/* ─── Confirmation / bulk table ──────────────────────────────────────────────*/
.confirm-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  color: #333;
}
.table-wrap {
  overflow-x: auto;
  border-radius: 6px;
  border: 1px solid #e0d0da;
  margin-bottom: 12px;
  -webkit-overflow-scrolling: touch;
}
.bulk-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 480px;
}
.bulk-table thead tr {
  background: #f3e6ef;
}
.bulk-table th {
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: #8b0057;
  border: 0;
  border-bottom: 1px solid #e0d0da;
  white-space: nowrap;
}
.bulk-table td {
  padding: 7px 10px;
  border: 0;
  border-bottom: 1px solid #f0e8f0;
  color: #333;
}
.row-even {
  background: #fff;
}
.row-odd {
  background: #fdf8fc;
}
.sheet-badge {
  display: inline-block;
  background: #f3e6ef;
  color: #8b0057;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.record-count {
  font-size: 12px;
  color: #888;
  margin-bottom: 12px;
}

/* ─── Manual entry table ─────────────────────────────────────────────────────*/
.manual-table-wrap {
  margin-top: 4px;
}
.manual-table td {
  padding: 4px 5px;
  vertical-align: middle;
}
.manual-table th {
  white-space: nowrap;
}

.cell-input {
  width: 100%;
  min-width: 90px;
  height: 32px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 12px;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.cell-input:focus {
  outline: none;
  border-color: #8b0057;
}

/* Duplicate serial highlight — applied via :class binding */
.cell-input--error {
  border-color: #d0011b;
  background: #fff5f5;
}
.cell-input--error:focus {
  border-color: #d0011b;
  outline: none;
}

.cell-select {
  width: 100%;
  min-width: 120px;
  height: 32px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 4px;
  font-size: 12px;
  background: #fff;
  box-sizing: border-box;
}
.cell-select:focus {
  outline: none;
  border-color: #8b0057;
}

.remove-cell {
  width: 28px;
  text-align: center;
}
.remove-row-btn {
  background: none;
  border: 1px solid #e0b0c8;
  border-radius: 50%;
  color: #8b0057;
  width: 22px;
  height: 22px;
  font-size: 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.remove-row-btn:hover {
  background: #fdecea;
}

.manual-table-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.add-row-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #8b0057;
  background: #fff;
  color: #8b0057;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.add-row-btn:hover {
  background: #f3e6ef;
}

/* ─── Buttons ────────────────────────────────────────────────────────────────*/
.bulk-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.submit-btn {
  padding: 10px 22px;
  background-color: #8b0057;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}
.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
.reset-btn {
  padding: 9px 20px;
  background: #fff;
  color: #8b0057;
  border: 1.5px solid #8b0057;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
}

/* ─── Form inputs ────────────────────────────────────────────────────────────*/
.form-container {
  padding: 16px;
}

.input-full {
  display: block;
  width: 100%;
  height: 36px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 14px;
}
.input-full:focus {
  outline: none;
  border-color: #8b0057;
}

.input-half {
  display: block;
  width: 100%;
  max-width: 300px;
  height: 36px;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 14px;
}
.input-half:focus {
  outline: none;
  border-color: #8b0057;
}

.input-div {
  margin-bottom: 16px;
}
.radio-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 6px;
}
.radio-row input[type="radio"],
.radio-row input[type="checkbox"] {
  margin-top: 3px;
  flex-shrink: 0;
  accent-color: #8b0057;
}
.radio-row label {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
}

.device-status-container {
  margin-bottom: 16px;
}

.is-invalid {
  border-color: rgb(208, 1, 27);
}
.error {
  color: rgb(208, 1, 27);
  font-size: 12px;
  display: block;
  margin-top: 3px;
}
.mandatory::after {
  content: " *";
  color: rgb(208, 1, 27);
}

.border-div {
  border-bottom: 2px solid lightgray;
  margin: 20px 0;
}
.submit-btn-wrap {
  margin: 20px 0;
}
.device-Comments-container {
  margin-top: 12px;
}

/* ─── Responsive breakpoints ─────────────────────────────────────────────────*/

/* Tablet — ≤ 768px */
@media (max-width: 768px) {
  .smartsheetform {
    padding: 12px 8px;
  }
  .card-white {
    max-width: 100%;
  }
  .privacy-notice {
    max-width: 100%;
    font-size: 12px;
  }
  .section-body {
    padding: 12px;
  }
  .form-container {
    padding: 12px 8px;
  }
  .drop-zone {
    padding: 20px 12px;
  }
  .title {
    font-size: clamp(16px, 5vw, 22px);
  }
  .input-half {
    max-width: 100%;
  }
  .bulk-table {
    min-width: 420px;
  }
}

/* Mobile — ≤ 480px */
@media (max-width: 480px) {
  .smartsheetform {
    padding: 8px 4px;
  }
  .logo-wrap {
    width: 120px;
  }
  .title {
    font-size: 17px;
  }
  .section-header {
    padding: 10px 12px;
    font-size: 13px;
  }
  .section-body {
    padding: 10px;
  }
  .form-container {
    padding: 10px 6px;
  }
  .submit-btn,
  .reset-btn {
    width: 100%;
    text-align: center;
  }
  .bulk-actions {
    flex-direction: column;
  }
  .manual-table-footer {
    flex-direction: column;
    align-items: flex-start;
  }
  .drop-zone {
    padding: 16px 10px;
    font-size: 13px;
  }
  .drop-icon {
    font-size: 26px;
  }
  .bulk-table {
    font-size: 12px;
    min-width: 360px;
  }
  .cell-input,
  .cell-select {
    font-size: 11px;
    min-width: 70px;
  }
  .privacy-notice {
    font-size: 11px;
    padding: 10px 12px;
  }
  .radio-row label {
    font-size: 13px;
  }
}
</style>